import { z } from 'zod';
import { listInput } from './list';
import { receiptPriceObservation } from './purchasing';
import { isoDate } from './date';

// Constructing a contract has no side effects. The annotations let browser builds omit
// unused server-only validators while keeping every imported strict parser intact.

/** Pricing's small phone page, independent of the catalogue's total size. */
export const pricingCurrentInput = /* @__PURE__ */ (() =>
  listInput.extend({
    limit: z.coerce.number().int().min(1).max(25).default(25),
  }))();
export type PricingCurrentInput = z.input<typeof pricingCurrentInput>;

export const pricingRounding = /* @__PURE__ */ (() =>
  z.enum(['cent', 'nickel', 'quarter', 'dollar', 'ending95']))();
export const pricingGoalMode = /* @__PURE__ */ (() =>
  z.enum(['category', 'own', 'never']))();
export const pricingGoalBps = /* @__PURE__ */ (() => z.number().int().min(0).max(9900))();
const pricingRevision = /* @__PURE__ */ (() => z.string().regex(/^[a-f0-9]{64}$/))();
const pricingId = /* @__PURE__ */ (() => z.string().uuid())();
const pricingCategoryKey = /* @__PURE__ */ (() => z.string().min(1).max(2000))();
export const pricingScope = /* @__PURE__ */ (() =>
  z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('item'), itemId: pricingId }).strict(),
    z.object({ kind: z.literal('category'), categoryKey: pricingCategoryKey }).strict(),
    z.object({ kind: z.literal('rounding') }).strict(),
    z.object({ kind: z.literal('tolerance') }).strict(),
  ]))();
export type PricingScope = z.infer<typeof pricingScope>;
const policyKeys = /* @__PURE__ */ (() => ({
  clientId: pricingId,
  mutationId: pricingId,
  expectedRevision: pricingRevision,
}))();
export const pricingItemPolicyInput = /* @__PURE__ */ (() =>
  z.object({ itemId: pricingId }).strict())();
export const pricingCategoryPolicyInput = /* @__PURE__ */ (() =>
  z.object({ categoryKey: pricingCategoryKey }).strict())();
export const pricingItemGoalSetInput = /* @__PURE__ */ (() =>
  z
    .object({
      ...policyKeys,
      itemId: pricingId,
      mode: pricingGoalMode,
      ownGoalBps: pricingGoalBps.nullable(),
    })
    .strict()
    .refine((p) => (p.mode === 'own' ? p.ownGoalBps !== null : p.ownGoalBps === null), {
      message: 'Own mode requires a goal; Category and Never require no own goal.',
      path: ['ownGoalBps'],
    }))();
export type PricingItemGoalSetInput = z.infer<typeof pricingItemGoalSetInput>;
export const pricingCategoryGoalSetInput = /* @__PURE__ */ (() =>
  z
    .object({
      ...policyKeys,
      categoryKey: pricingCategoryKey,
      goalBps: pricingGoalBps.nullable(),
    })
    .strict())();
export type PricingCategoryGoalSetInput = z.infer<typeof pricingCategoryGoalSetInput>;
export const pricingRoundingSetInput = /* @__PURE__ */ (() =>
  z.object({ ...policyKeys, rule: pricingRounding }).strict())();
export type PricingRoundingSetInput = z.infer<typeof pricingRoundingSetInput>;
export const pricingToleranceBps = /* @__PURE__ */ (() =>
  z.number().int().min(0).max(10000))();
export const pricingToleranceSetInput = /* @__PURE__ */ (() =>
  z.object({ ...policyKeys, toleranceBps: pricingToleranceBps }).strict())();
export type PricingToleranceSetInput = z.infer<typeof pricingToleranceSetInput>;
export const pricingToleranceResult = /* @__PURE__ */ (() =>
  z
    .object({
      storedToleranceBps: pricingToleranceBps.nullable(),
      effectiveToleranceBps: pricingToleranceBps,
      revision: pricingRevision,
    })
    .strict())();
export const pricingReceiptEvidenceInput = /* @__PURE__ */ (() =>
  z
    .object({
      purchaseOrderId: pricingId,
      offset: z.number().int().nonnegative().max(1000000).default(0),
    })
    .strict())();
export const pricingReceiptEvidenceResult = /* @__PURE__ */ (() =>
  z
    .object({
      rows: z.array(receiptPriceObservation).max(25),
      total: z.number().int().nonnegative().safe(),
    })
    .strict())();

// Vendor history deliberately has its own exact keyset protocol. PO evidence keeps
// its existing offset envelope and millisecond display timestamp unchanged.
export const pricingVarianceCursor = /* @__PURE__ */ (() =>
  z
    .object({
      receivedAt: z.string().datetime({ precision: 6 }),
      id: pricingId,
    })
    .strict())();
export const pricingVarianceInput = /* @__PURE__ */ (() =>
  z
    .object({
      vendorId: pricingId,
      from: isoDate.optional(),
      to: isoDate.optional(),
      cursor: pricingVarianceCursor.optional(),
    })
    .strict()
    .refine(
      (p) =>
        (p.from === undefined && p.to === undefined) ||
        (p.from !== undefined &&
          p.to !== undefined &&
          p.from <= p.to &&
          p.to < '9999-12-31'),
      {
        message:
          'Choose both From and Through in order, with a resolvable following day.',
        path: ['to'],
      },
    ))();
export type PricingVarianceInput = z.infer<typeof pricingVarianceInput>;
const varianceCount = /* @__PURE__ */ (() => z.number().int().nonnegative().safe())();
const varianceRow = /* @__PURE__ */ (() =>
  receiptPriceObservation
    .extend({
      purchaseOrderNumber: z.number().int().nullable(),
      lineName: z.string().nullable(),
      linePosition: z.number().int().nullable(),
      deltaPercentBps: z.number().int().safe().nullable(),
      deltaBelowPrecision: z.enum(['increase', 'decrease']).nullable(),
    })
    .superRefine((r, ctx) => {
      const fail = () =>
        ctx.addIssue({ code: 'custom', message: 'Inconsistent receipt comparison.' });
      if (r.classification === 'excluded') {
        if (
          r.exclusionReason === null ||
          r.deltaCents !== null ||
          r.deltaPercentBps !== null ||
          r.deltaBelowPrecision !== null
        )
          fail();
        return;
      }
      if (
        r.exclusionReason !== null ||
        r.orderedUnitCostCents === null ||
        r.orderedCostProvenance !== 'declared_ordered' ||
        r.receivedCostProvenance !== 'declared_this_receipt' ||
        r.deltaCents !== r.recordedUnitCostCents - r.orderedUnitCostCents
      ) {
        fail();
        return;
      }
      if (r.orderedUnitCostCents === 0) {
        if (r.deltaPercentBps !== null || r.deltaBelowPrecision !== null) fail();
      } else {
        if (r.deltaPercentBps === null) fail();
        const hint =
          r.deltaPercentBps === 0 && r.deltaCents !== 0
            ? r.deltaCents! > 0
              ? 'increase'
              : 'decrease'
            : null;
        if (r.deltaBelowPrecision !== hint) fail();
      }
    }))();
export const pricingVarianceResult = /* @__PURE__ */ (() =>
  z
    .object({
      vendor: z.object({ id: pricingId, name: z.string() }).strict(),
      period: z
        .object({
          from: isoDate,
          through: isoDate,
          timezone: z.string().min(1),
          start: z.string().datetime(),
          end: z.string().datetime(),
        })
        .strict(),
      summary: z
        .object({
          flaggedLines: varianceCount,
          comparableLines: varianceCount,
          excludedLines: varianceCount,
          unknownOrderedLines: varianceCount,
          inheritedOnlyLines: varianceCount,
          counterLines: varianceCount,
          zeroOrderedLines: varianceCount,
          totalLines: varianceCount,
          rateBps: z.number().int().min(0).max(10000).nullable(),
          rateBelowPrecision: z.boolean(),
        })
        .strict(),
      total: varianceCount,
      rows: z.array(varianceRow).max(25),
      nextCursor: pricingVarianceCursor.nullable(),
    })
    .strict()
    .superRefine((r, ctx) => {
      const s = r.summary;
      const rate =
        s.comparableLines === 0
          ? null
          : Number((BigInt(s.flaggedLines) * 10000n) / BigInt(s.comparableLines));
      if (
        s.flaggedLines > s.comparableLines ||
        s.zeroOrderedLines > s.comparableLines ||
        s.excludedLines !==
          s.counterLines + s.unknownOrderedLines + s.inheritedOnlyLines ||
        s.totalLines !== s.comparableLines + s.excludedLines ||
        s.totalLines > r.total ||
        s.rateBps !== rate ||
        s.rateBelowPrecision !== (s.flaggedLines > 0 && rate === 0) ||
        r.rows.length > r.total ||
        r.rows.some((row) => row.vendorId !== r.vendor.id) ||
        new Set(r.rows.map((row) => row.id)).size !== r.rows.length ||
        (r.nextCursor !== null &&
          (r.rows.length !== 25 || r.rows.at(-1)?.id !== r.nextCursor.id)) ||
        r.period.from > r.period.through ||
        r.period.start >= r.period.end
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Inconsistent variance summary, period or page.',
        });
      }
    }))();
export type PricingVarianceResult = z.infer<typeof pricingVarianceResult>;
export const pricingPolicyContext = /* @__PURE__ */ (() =>
  z
    .object({
      storedRounding: pricingRounding.nullable(),
      effectiveRounding: pricingRounding,
      roundingRevision: pricingRevision,
    })
    .strict())();
export const pricingRowPolicy = /* @__PURE__ */ (() =>
  pricingPolicyContext
    .extend({
      goalMode: pricingGoalMode,
      ownGoalBps: pricingGoalBps.nullable(),
      goalPolicyRevision: z.number().int().positive(),
      categoryGoalBps: pricingGoalBps.nullable(),
      categoryPolicyRevision: z.number().int().positive().nullable(),
      effectiveGoalBps: pricingGoalBps.nullable(),
      effectiveGoalSource: z.enum(['category', 'own', 'never', 'none']),
      goalPriceCents: z.number().int().min(0).max(100000000).nullable(),
      goalPriceStatus: z.enum(['ready', 'no_goal', 'never', 'no_cost', 'overflow']),
    })
    .strict())();

export const pricingCurrentRow = /* @__PURE__ */ (() =>
  z
    .object({
      ...pricingRowPolicy.shape,
      id: z.string().uuid(),
      name: z.string(),
      sku: z.string().nullable(),
      category: z.string().nullable(),
      unitLabel: z.string().nullable(),
      vendorId: z.string().uuid().nullable(),
      vendorName: z.string().nullable(),
      unitPriceCents: z.number().int().safe(),
      lastCostCents: z.number().int().safe().nullable(),
      marginPercent: z.number().int().safe().nullable(),
      marginUnknownReason: z.enum(['no_cost', 'no_sell_price']).nullable(),
    })
    .strict())();
export type PricingCurrentRow = z.infer<typeof pricingCurrentRow>;
export const pricingCurrentResult = /* @__PURE__ */ (() =>
  z
    .object({
      rows: z.array(pricingCurrentRow).max(25),
      total: z.number().int().nonnegative().safe(),
    })
    .strict())();

export const pricingItemPolicyResult = /* @__PURE__ */ (() =>
  pricingCurrentRow
    .extend({ revision: pricingRevision, editable: z.boolean() })
    .strict())();
export type PricingItemPolicyResult = z.infer<typeof pricingItemPolicyResult>;
export const pricingCategoryPolicyResult = /* @__PURE__ */ (() =>
  pricingPolicyContext
    .extend({
      categoryKey: pricingCategoryKey,
      goalBps: pricingGoalBps.nullable(),
      policyRevision: z.number().int().nonnegative(),
      revision: pricingRevision,
      affectedCount: z.number().int().nonnegative().max(5001),
      overCapacity: z.boolean(),
    })
    .strict())();
export type PricingCategoryPolicyResult = z.infer<typeof pricingCategoryPolicyResult>;
export const pricingRoundingResult = /* @__PURE__ */ (() =>
  pricingPolicyContext
    .extend({
      revision: pricingRevision,
      affectedCount: z.number().int().nonnegative().max(5001),
      overCapacity: z.boolean(),
    })
    .strict())();
export type PricingRoundingResult = z.infer<typeof pricingRoundingResult>;
export const pricingPolicyWriteResult = /* @__PURE__ */ (() =>
  z
    .object({
      changed: z.boolean(),
      operationId: pricingId.nullable(),
      appliedAt: z.string().datetime(),
      scope: pricingScope,
      revision: pricingRevision,
    })
    .strict())();
export type PricingPolicyWriteResult = z.infer<typeof pricingPolicyWriteResult>;
export const pricingPolicyHistoryInput = /* @__PURE__ */ (() =>
  z
    .object({
      scope: pricingScope,
      offset: z.number().int().nonnegative().max(1000000).default(0),
    })
    .strict())();
export type PricingPolicyHistoryInput = z.input<typeof pricingPolicyHistoryInput>;
export const pricingPolicyHistoryRow = /* @__PURE__ */ (() =>
  z
    .object({
      id: pricingId,
      kind: z.enum(['goal_change', 'tolerance_change']),
      scopeKind: z.enum(['item', 'category', 'rounding', 'tolerance']),
      targetKey: z.string().min(1).max(2000),
      actorId: pricingId,
      actorName: z.string().nullable(),
      createdAt: z.string().datetime(),
      itemId: pricingId.nullable(),
      categoryKey: z.string().nullable(),
      beforeMode: pricingGoalMode.nullable(),
      afterMode: pricingGoalMode.nullable(),
      beforeGoalBps: pricingGoalBps.nullable(),
      afterGoalBps: pricingGoalBps.nullable(),
      beforeEffectiveGoalBps: pricingGoalBps.nullable(),
      afterEffectiveGoalBps: pricingGoalBps.nullable(),
      beforeGoalSource: z.enum(['category', 'own', 'never', 'none']).nullable(),
      afterGoalSource: z.enum(['category', 'own', 'never', 'none']).nullable(),
      beforeToleranceBps: pricingToleranceBps.nullable(),
      afterToleranceBps: pricingToleranceBps.nullable(),
      beforeRounding: pricingRounding.nullable(),
      afterRounding: pricingRounding.nullable(),
      unitPriceCents: z.number().int().nonnegative().nullable(),
      lastCostCents: z.number().int().nonnegative().nullable(),
      beforePolicyRevision: z.number().int().positive().nullable(),
      afterPolicyRevision: z.number().int().positive().nullable(),
    })
    .strict())();
export const pricingPolicyHistoryResult = /* @__PURE__ */ (() =>
  z
    .object({
      rows: z.array(pricingPolicyHistoryRow).max(25),
      total: z.number().int().nonnegative().safe(),
    })
    .strict())();
export type PricingPolicyHistoryResult = z.infer<typeof pricingPolicyHistoryResult>;

const reviewIdentity = /* @__PURE__ */ (() => ({
  reviewerId: pricingId,
  reviewerName: z.string().nullable(),
  reviewedAt: z.string().datetime(),
}))();
export const pricingUndoLocator = /* @__PURE__ */ (() =>
  z
    .object({
      operationId: pricingId,
      memberId: pricingId,
      actorId: pricingId,
      actorName: z.string().nullable(),
      at: z.string().datetime(),
      note: z.string().min(1).max(500).nullable(),
    })
    .strict())();
export const pricingRaiseReviewState = /* @__PURE__ */ (() =>
  z.discriminatedUnion('status', [
    z.object({ status: z.literal('unreviewed') }).strict(),
    z
      .object({ status: z.literal('reviewed'), ...reviewIdentity, undo: z.null() })
      .strict(),
    z
      .object({
        status: z.literal('undone'),
        ...reviewIdentity,
        undo: pricingUndoLocator,
      })
      .strict(),
  ]))();
export type PricingRaiseReviewState = z.infer<typeof pricingRaiseReviewState>;
export const pricingRaiseKeepInput = /* @__PURE__ */ (() =>
  z
    .object({ raiseMemberId: pricingId, clientId: pricingId, mutationId: pricingId })
    .strict())();
export type PricingRaiseKeepInput = z.infer<typeof pricingRaiseKeepInput>;
export const pricingRaiseUndoPreviewInput = /* @__PURE__ */ (() =>
  z.object({ raiseMemberId: pricingId }).strict())();
export const pricingRaiseUndoInput = /* @__PURE__ */ (() =>
  z
    .object({
      raiseMemberId: pricingId,
      ...policyKeys,
      note: z
        .string()
        .trim()
        .max(500)
        .nullish()
        .transform((value) => value || null),
    })
    .strict())();
export type PricingRaiseUndoInput = z.infer<typeof pricingRaiseUndoInput>;
export const pricingRaiseUndoPreviewResult = /* @__PURE__ */ (() =>
  z
    .object({
      protocolVersion: z.literal(1),
      raiseMemberId: pricingId,
      raiseOperationId: pricingId,
      itemId: pricingId,
      itemName: z.string(),
      beforeSellCents: z.number().int().min(0).max(100000000),
      afterSellCents: z.number().int().min(0).max(100000000),
      currentSellCents: z.number().int().min(0).max(100000000),
      restoreSellCents: z.number().int().min(0).max(100000000),
      currentPriceVersion: z.number().int().positive().max(2147483647),
      lastCostCents: z.number().int().min(0).max(100000000).nullable(),
      status: z.enum([
        'eligible',
        'retired',
        'later_price_change',
        'already_undone',
        'witness_exhausted',
      ]),
      revision: pricingRevision,
      review: pricingRaiseReviewState,
    })
    .strict())();
export type PricingRaiseUndoPreviewResult = z.infer<typeof pricingRaiseUndoPreviewResult>;
const reviewReply = /* @__PURE__ */ (() => ({
  protocolVersion: z.literal(1),
  raiseMemberId: pricingId,
  itemId: pricingId,
  changed: z.boolean(),
  reviewerId: pricingId,
  reviewedAt: z.string().datetime(),
}))();
export const pricingRaiseWriteResult = /* @__PURE__ */ (() =>
  z.discriminatedUnion('action', [
    z
      .object({
        ...reviewReply,
        action: z.literal('keep'),
        status: z.enum(['reviewed', 'undone']),
        undoOperationId: pricingId.nullable(),
        undoMemberId: pricingId.nullable(),
      })
      .strict()
      .refine(
        (reply) =>
          reply.status === 'reviewed'
            ? reply.undoOperationId === null && reply.undoMemberId === null
            : reply.undoOperationId !== null && reply.undoMemberId !== null,
        { message: 'Review status and inverse identifiers must agree.' },
      ),
    z
      .object({
        ...reviewReply,
        action: z.literal('undo'),
        status: z.literal('undone'),
        changed: z.literal(true),
        undoOperationId: pricingId,
        undoMemberId: pricingId,
      })
      .strict(),
  ]))();
export type PricingRaiseWriteResult = z.infer<typeof pricingRaiseWriteResult>;

const historyKind = /* @__PURE__ */ (() =>
  z.enum([
    'goal_change',
    'tolerance_change',
    'manual',
    'auto_raise',
    'goal_raise',
    'bulk',
    'undo',
  ]))();
const historyOffset = /* @__PURE__ */ (() =>
  z.number().int().min(0).max(1000000).default(0))();
export const pricingHistoryInput = /* @__PURE__ */ (() =>
  z
    .object({
      itemId: pricingId.optional(),
      operationId: pricingId.optional(),
      kind: historyKind.optional(),
      offset: historyOffset,
    })
    .strict())();
export const pricingHistoryMembersInput = /* @__PURE__ */ (() =>
  z
    .object({
      operationId: pricingId,
      itemId: pricingId.optional(),
      offset: historyOffset,
    })
    .strict())();
const historyBase = /* @__PURE__ */ (() => ({
  id: pricingId,
  operationId: pricingId,
  ordinal: z.number().int().nonnegative(),
  itemId: pricingId.nullable(),
  itemName: z.string().nullable(),
}))();
const historyPrice = /* @__PURE__ */ (() => ({
  ...historyBase,
  itemId: pricingId,
  itemName: z.string(),
  itemKind: z.enum(['material', 'service', 'labor', 'fee']),
  beforeSellCents: z.number().int().min(0).max(100000000),
  afterSellCents: z.number().int().min(0).max(100000000),
  beforePriceVersion: z.number().int().positive(),
  afterPriceVersion: z.number().int().positive(),
  lastCostCents: z.number().int().min(0).max(100000000).nullable(),
}))();
export const pricingHistoryMember = /* @__PURE__ */ (() =>
  z.discriminatedUnion('kind', [
    z
      .object({
        ...historyPrice,
        kind: z.literal('bulk'),
        goalBps: pricingGoalBps.nullable(),
        goalSource: z.enum(['own', 'category', 'never', 'none']),
        rounding: pricingRounding,
        undo: pricingUndoLocator.nullable(),
      })
      .strict(),
    z
      .object({
        ...historyPrice,
        kind: z.literal('goal_raise'),
        lastCostCents: z.number().int().min(0).max(100000000),
        goalBps: pricingGoalBps,
        goalSource: z.enum(['own', 'category']),
        rounding: pricingRounding,
        undo: pricingUndoLocator.nullable(),
      })
      .strict(),
    z.object({ ...historyPrice, kind: z.literal('manual') }).strict(),
    z
      .object({
        ...historyPrice,
        kind: z.literal('undo'),
        raiseMemberId: pricingId,
        raiseOperationId: pricingId,
        note: z.string().min(1).max(500).nullable(),
      })
      .strict(),
    z
      .object({
        ...historyPrice,
        kind: z.literal('auto_raise'),
        review: pricingRaiseReviewState,
        previousLastCostCents: z.number().int().min(0).max(100000000).nullable(),
        goalBps: pricingGoalBps,
        goalSource: z.enum(['own', 'category']),
        rounding: pricingRounding,
        source: z
          .object({
            observationId: pricingId,
            purchaseOrderId: pricingId,
            poNumber: z.number().int(),
            counter: z.boolean(),
            purchaseOrderLineId: pricingId,
            movementId: pricingId,
            vendorId: pricingId,
            vendorName: z.string(),
            locationId: pricingId,
            locationName: z.string(),
            actorId: pricingId,
            receivedAt: z.string().datetime(),
            receivedCostProvenance: z.enum(['declared_this_receipt', 'inherited_line']),
          })
          .strict(),
      })
      .strict(),
    z
      .object({
        ...historyBase,
        kind: z.literal('goal_change'),
        ...pricingPolicyHistoryRow.pick({
          categoryKey: true,
          beforeMode: true,
          afterMode: true,
          beforeGoalBps: true,
          afterGoalBps: true,
          beforeEffectiveGoalBps: true,
          afterEffectiveGoalBps: true,
          beforeGoalSource: true,
          afterGoalSource: true,
          beforeRounding: true,
          afterRounding: true,
          unitPriceCents: true,
          lastCostCents: true,
          beforePolicyRevision: true,
          afterPolicyRevision: true,
        }).shape,
      })
      .strict(),
    z
      .object({
        ...historyBase,
        kind: z.literal('tolerance_change'),
        beforeToleranceBps: pricingToleranceBps.nullable(),
        afterToleranceBps: pricingToleranceBps,
      })
      .strict(),
  ]))();
export type PricingHistoryMember = z.infer<typeof pricingHistoryMember>;
export const pricingHistoryOperation = /* @__PURE__ */ (() =>
  z
    .object({
      id: pricingId,
      kind: historyKind,
      scopeKind: z.string(),
      targetKey: z.string(),
      sourcePurchaseOrderId: pricingId.nullable(),
      actorId: pricingId,
      actorName: z.string().nullable(),
      createdAt: z.string().datetime(),
      memberCount: z.number().int().nonnegative(),
      partCount: z.number().int().nonnegative(),
      members: z.array(pricingHistoryMember).max(3),
      bulk: z.lazy(() => pricingBulkIntent).optional(),
      undoSourceOperationId: pricingId.optional(),
      undoSourceScopeKind: z.enum(['bulk', 'goal']).optional(),
    })
    .strict())();
export type PricingHistoryOperation = z.infer<typeof pricingHistoryOperation>;
export const pricingHistoryResult = /* @__PURE__ */ (() =>
  z
    .object({
      rows: z.array(pricingHistoryOperation).max(25),
      total: z.number().int().nonnegative(),
    })
    .strict())();
export const pricingHistoryMembersResult = /* @__PURE__ */ (() =>
  z
    .object({
      rows: z.array(pricingHistoryMember).max(25),
      total: z.number().int().nonnegative(),
    })
    .strict())();
export const pricingRaisedCountResult = /* @__PURE__ */ (() =>
  z
    .object({
      raiseCount: z.number().int().nonnegative(),
      partCount: z.number().int().nonnegative(),
    })
    .strict())();

// Deliberate goal changes share policy math, but never inherit a table's search/filter.
const goalId = /* @__PURE__ */ (() => pricingId.transform((id) => id.toLowerCase()))();
const goalCategorySelection = /* @__PURE__ */ (() =>
  z
    .object({
      kind: z.literal('category'),
      categoryKey: z
        .string()
        .max(2000)
        .nullable()
        .transform((key) => key || null),
    })
    .strict())();
const goalVendorSelection = /* @__PURE__ */ (() =>
  z.object({ kind: z.literal('vendor'), vendorId: goalId }).strict())();
export const pricingGoalSelection = /* @__PURE__ */ (() =>
  z.discriminatedUnion('kind', [
    goalCategorySelection,
    goalVendorSelection,
    z.object({ kind: z.literal('item'), itemId: goalId }).strict(),
  ]))();
export type PricingGoalSelection = z.infer<typeof pricingGoalSelection>;
const goalIds = /* @__PURE__ */ (() =>
  z
    .array(goalId)
    .max(5000)
    .refine((ids) => new Set(ids).size === ids.length, 'Duplicate parts are not allowed.')
    .transform((ids) => [...ids].sort()))();
export const pricingGoalScopesInput = /* @__PURE__ */ (() =>
  z
    .object({
      kind: z.enum(['category', 'vendor']),
      search: z.string().trim().max(200).default(''),
      offset: historyOffset,
    })
    .strict())();
export const pricingGoalScopesResult = /* @__PURE__ */ (() =>
  z
    .object({
      options: z
        .array(
          z
            .object({
              selection: z.discriminatedUnion('kind', [
                goalCategorySelection,
                goalVendorSelection,
              ]),
              label: z.string(),
            })
            .strict(),
        )
        .max(25),
      total: z.number().int().nonnegative().safe(),
      unsupportedCategoryCount: z.number().int().nonnegative().safe(),
    })
    .strict())();
export type PricingGoalScopesResult = z.infer<typeof pricingGoalScopesResult>;
export const pricingGoalPreviewInput = /* @__PURE__ */ (() =>
  z
    .object({
      selection: pricingGoalSelection,
      offset: historyOffset,
      expectedRevision: pricingRevision.optional(),
    })
    .strict()
    .refine(
      (p) => p.offset === 0 || p.expectedRevision !== undefined,
      'Later pages require the reviewed proposal revision.',
    ))();
export type PricingGoalPreviewInput = z.infer<typeof pricingGoalPreviewInput>;
export const pricingGoalStatus = /* @__PURE__ */ (() =>
  z.enum([
    'will_raise',
    'at_or_above_goal',
    'no_goal',
    'never',
    'no_last_cost',
    'no_sell_price',
    'ineligible',
    'overflow',
    'witness_exhausted',
  ]))();
export const pricingGoalSummary = /* @__PURE__ */ (() =>
  z
    .object({
      selectedCount: z.number().int().min(0).max(5000),
      changedCount: z.number().int().min(0).max(5000),
      skippedCount: z.number().int().min(0).max(5000),
      blockingCount: z.number().int().min(0).max(5000),
    })
    .strict()
    .refine(
      (s) => s.selectedCount === s.changedCount + s.skippedCount + s.blockingCount,
      'Proposal counts must balance.',
    ))();
export const pricingGoalPreviewRow = /* @__PURE__ */ (() =>
  z
    .object({
      id: goalId,
      name: z.string(),
      category: z.string().nullable(),
      vendorId: goalId.nullable(),
      beforeSellCents: z.number().int().min(0).max(100000000),
      afterSellCents: z.number().int().min(0).max(100000000),
      currentPriceVersion: z.number().int().positive().max(2147483647),
      lastCostCents: z.number().int().min(0).max(100000000).nullable(),
      beforeMarginPercent: z.number().int().safe().nullable(),
      afterMarginPercent: z.number().int().safe().nullable(),
      effectiveGoalBps: pricingGoalBps.nullable(),
      effectiveGoalSource: z.enum(['own', 'category', 'never', 'none']),
      rounding: pricingRounding,
      status: pricingGoalStatus,
    })
    .strict())();
export const pricingGoalPreviewResult = /* @__PURE__ */ (() =>
  z
    .object({
      protocolVersion: z.literal(1),
      selection: pricingGoalSelection,
      reviewedItemIds: goalIds,
      revision: pricingRevision,
      summary: pricingGoalSummary,
      rows: z.array(pricingGoalPreviewRow).max(25),
    })
    .strict()
    .refine(
      (p) => p.reviewedItemIds.length === p.summary.selectedCount,
      'The complete cohort must agree with its count.',
    ))();
export type PricingGoalPreviewResult = z.infer<typeof pricingGoalPreviewResult>;
export const pricingGoalApplyInput = /* @__PURE__ */ (() =>
  z
    .object({
      selection: pricingGoalSelection,
      reviewedItemIds: goalIds,
      ...policyKeys,
    })
    .strict())();
export type PricingGoalApplyInput = z.infer<typeof pricingGoalApplyInput>;
export const pricingGoalApplyResult = /* @__PURE__ */ (() =>
  z
    .object({
      protocolVersion: z.literal(1),
      action: z.literal('goal_apply'),
      selectionHash: pricingRevision,
      revision: pricingRevision,
      operationId: goalId.nullable(),
      selectedCount: z.number().int().min(0).max(5000),
      changedCount: z.number().int().min(0).max(5000),
      skippedCount: z.number().int().min(0).max(5000),
    })
    .strict()
    .refine(
      (p) =>
        p.selectedCount === p.changedCount + p.skippedCount &&
        (p.changedCount === 0 ? p.operationId === null : p.operationId !== null),
      'Actual change counts and operation identity must agree.',
    ))();
export type PricingGoalApplyResult = z.infer<typeof pricingGoalApplyResult>;
export const pricingGoalUndoPreviewInput = /* @__PURE__ */ (() =>
  z.object({ goalMemberId: goalId }).strict())();
export const pricingGoalUndoInput = /* @__PURE__ */ (() =>
  z
    .object({
      goalMemberId: goalId,
      ...policyKeys,
      note: z
        .string()
        .trim()
        .max(500)
        .nullish()
        .transform((value) => value || null),
    })
    .strict())();
export type PricingGoalUndoInput = z.infer<typeof pricingGoalUndoInput>;
export const pricingGoalUndoPreviewResult = /* @__PURE__ */ (() =>
  z
    .object({
      protocolVersion: z.literal(1),
      goalMemberId: goalId,
      goalOperationId: goalId,
      itemId: goalId,
      itemName: z.string(),
      beforeSellCents: z.number().int().min(0).max(100000000),
      afterSellCents: z.number().int().min(0).max(100000000),
      currentSellCents: z.number().int().min(0).max(100000000),
      restoreSellCents: z.number().int().min(0).max(100000000),
      currentPriceVersion: z.number().int().positive().max(2147483647),
      lastCostCents: z.number().int().min(0).max(100000000).nullable(),
      status: z.enum([
        'eligible',
        'already_undone',
        'retired',
        'later_price_change',
        'witness_exhausted',
      ]),
      revision: pricingRevision,
      undo: pricingUndoLocator.nullable(),
    })
    .strict())();
export type PricingGoalUndoPreviewResult = z.infer<typeof pricingGoalUndoPreviewResult>;
export const pricingGoalUndoResult = /* @__PURE__ */ (() =>
  z
    .object({
      protocolVersion: z.literal(1),
      action: z.literal('goal_undo'),
      goalMemberId: goalId,
      itemId: goalId,
      changed: z.literal(true),
      undoOperationId: goalId,
      undoMemberId: goalId,
    })
    .strict())();
export type PricingGoalUndoResult = z.infer<typeof pricingGoalUndoResult>;
/** Internal saved refusal; only a committed keyed command can establish this result. */
export const pricingGoalRejectResult = /* @__PURE__ */ (() =>
  z
    .object({
      protocolVersion: z.literal(1),
      action: z.enum(['goal_apply', 'goal_undo']),
      selectionHash: pricingRevision,
      revision: pricingRevision,
      reason: z.enum(['goal_stale', 'goal_capacity', 'goal_overflow', 'goal_witness']),
    })
    .strict())();

// Deliberate percentage changes and operation inverses use the same complete-cohort protocol.
export const pricingBulkSelection = /* @__PURE__ */ (() =>
  z.discriminatedUnion('kind', [goalCategorySelection, goalVendorSelection]))();
export type PricingBulkSelection = z.infer<typeof pricingBulkSelection>;
const bulkIntentFields = /* @__PURE__ */ (() => ({
  selection: pricingBulkSelection,
  direction: z.enum(['raise', 'lower']),
  magnitudeBps: z.number().int().min(10).max(5000),
}))();
export const pricingBulkIntent = /* @__PURE__ */ (() =>
  z.object(bulkIntentFields).strict())();
export type PricingBulkIntent = z.infer<typeof pricingBulkIntent>;
export const pricingBulkPreviewInput = /* @__PURE__ */ (() =>
  z
    .object({
      ...bulkIntentFields,
      offset: historyOffset,
      expectedRevision: pricingRevision.optional(),
    })
    .strict()
    .refine(
      (p) => p.offset === 0 || p.expectedRevision !== undefined,
      'Later pages require the reviewed proposal revision.',
    ))();
export type PricingBulkPreviewInput = z.infer<typeof pricingBulkPreviewInput>;
const batchCount = /* @__PURE__ */ (() => z.number().int().min(0).max(5000))();
export const pricingBulkPreviewRow = /* @__PURE__ */ (() =>
  pricingGoalPreviewRow
    .omit({ status: true })
    .extend({
      status: z.enum([
        'will_change',
        'rounded_no_change',
        'no_sell_price',
        'ineligible',
        'overflow',
        'witness_exhausted',
      ]),
      warnings: z
        .object({
          noLastCost: z.boolean(),
          noGoal: z.boolean(),
          belowGoal: z.boolean().nullable(),
        })
        .strict(),
    })
    .strict())();
export const pricingBulkPreviewResult = /* @__PURE__ */ (() =>
  z
    .object({
      protocolVersion: z.literal(1),
      ...bulkIntentFields,
      reviewedItemIds: goalIds,
      revision: pricingRevision,
      rounding: pricingRounding,
      summary: pricingGoalSummary,
      warnings: z
        .object({
          noLastCostCount: batchCount,
          noGoalCount: batchCount,
          belowGoalCount: batchCount,
        })
        .strict(),
      rows: z.array(pricingBulkPreviewRow).max(25),
    })
    .strict()
    .refine(
      (p) => p.reviewedItemIds.length === p.summary.selectedCount,
      'The complete cohort must agree with its count.',
    ))();
export type PricingBulkPreviewResult = z.infer<typeof pricingBulkPreviewResult>;
export const pricingBulkApplyInput = /* @__PURE__ */ (() =>
  z
    .object({
      ...bulkIntentFields,
      reviewedItemIds: goalIds,
      ...policyKeys,
    })
    .strict())();
export type PricingBulkApplyInput = z.infer<typeof pricingBulkApplyInput>;
const batchReplyFields = /* @__PURE__ */ (() => ({
  protocolVersion: z.literal(1),
  selectionHash: pricingRevision,
  revision: pricingRevision,
  selectedCount: batchCount,
  changedCount: batchCount,
  skippedCount: batchCount,
}))();
export const pricingBulkApplyResult = /* @__PURE__ */ (() =>
  z
    .object({
      ...batchReplyFields,
      action: z.literal('bulk_apply'),
      operationId: goalId.nullable(),
    })
    .strict()
    .refine(
      (p) =>
        p.selectedCount === p.changedCount + p.skippedCount &&
        (p.changedCount === 0 ? p.operationId === null : p.operationId !== null),
      'Actual change counts and operation identity must agree.',
    ))();
export type PricingBulkApplyResult = z.infer<typeof pricingBulkApplyResult>;
export const pricingOperationUndoPreviewInput = /* @__PURE__ */ (() =>
  z
    .object({
      operationId: goalId,
      offset: historyOffset,
      expectedRevision: pricingRevision.optional(),
    })
    .strict()
    .refine(
      (p) => p.offset === 0 || p.expectedRevision !== undefined,
      'Later pages require the reviewed proposal revision.',
    ))();
export type PricingOperationUndoPreviewInput = z.infer<
  typeof pricingOperationUndoPreviewInput
>;
export const pricingOperationUndoInput = /* @__PURE__ */ (() =>
  z
    .object({
      operationId: goalId,
      reviewedMemberIds: goalIds,
      eligibleMemberIds: goalIds,
      ...policyKeys,
      note: z
        .string()
        .trim()
        .max(500)
        .nullish()
        .transform((v) => v || null),
    })
    .strict()
    .refine((p) => {
      const original = new Set(p.reviewedMemberIds);
      return p.eligibleMemberIds.every((id) => original.has(id));
    }, 'Eligible members must belong to the complete reviewed original set.'))();
export type PricingOperationUndoInput = z.infer<typeof pricingOperationUndoInput>;
export const pricingOperationUndoStatus = /* @__PURE__ */ (() =>
  z.enum([
    'eligible',
    'already_undone',
    'retired',
    'later_price_change',
    'witness_exhausted',
  ]))();
export const pricingOperationUndoRow = /* @__PURE__ */ (() =>
  z
    .object({
      id: goalId,
      itemId: goalId,
      name: z.string(),
      beforeSellCents: z.number().int().min(0).max(100000000),
      afterSellCents: z.number().int().min(0).max(100000000),
      currentSellCents: z.number().int().min(0).max(100000000).nullable(),
      restoreSellCents: z.number().int().min(0).max(100000000),
      currentPriceVersion: z.number().int().positive().max(2147483647).nullable(),
      lastCostCents: z.number().int().min(0).max(100000000).nullable(),
      status: pricingOperationUndoStatus,
      undo: pricingUndoLocator.nullable(),
    })
    .strict())();
export const pricingOperationUndoPreviewResult = /* @__PURE__ */ (() =>
  z
    .object({
      protocolVersion: z.literal(1),
      operationId: goalId,
      sourceKind: z.enum(['bulk', 'goal_raise']),
      bulk: pricingBulkIntent.nullable(),
      revision: pricingRevision,
      reviewedMemberIds: goalIds,
      eligibleMemberIds: goalIds,
      summary: z
        .object({
          selectedCount: batchCount,
          eligibleCount: batchCount,
          skippedCount: batchCount,
          blockingCount: batchCount,
        })
        .strict()
        .refine(
          (p) => p.selectedCount === p.eligibleCount + p.skippedCount + p.blockingCount,
          'Undo proposal counts must balance.',
        ),
      reasons: z
        .object({
          alreadyUndone: batchCount,
          retired: batchCount,
          laterPriceChange: batchCount,
          witnessExhausted: batchCount,
        })
        .strict(),
      rows: z.array(pricingOperationUndoRow).max(25),
    })
    .strict()
    .refine((p) => {
      const originalIds = new Set(p.reviewedMemberIds);
      return (
        p.reviewedMemberIds.length === p.summary.selectedCount &&
        p.eligibleMemberIds.length === p.summary.eligibleCount &&
        p.eligibleMemberIds.every((id) => originalIds.has(id)) &&
        p.reasons.alreadyUndone + p.reasons.retired + p.reasons.laterPriceChange ===
          p.summary.skippedCount &&
        p.reasons.witnessExhausted === p.summary.blockingCount
      );
    }, 'Original, eligible and named reason counts must agree.'))();
export type PricingOperationUndoPreviewResult = z.infer<
  typeof pricingOperationUndoPreviewResult
>;
export const pricingOperationUndoResult = /* @__PURE__ */ (() =>
  z
    .object({
      ...batchReplyFields,
      action: z.literal('operation_undo'),
      sourceOperationId: goalId,
      undoOperationId: goalId.nullable(),
    })
    .strict()
    .refine(
      (p) =>
        p.selectedCount === p.changedCount + p.skippedCount &&
        (p.changedCount === 0 ? p.undoOperationId === null : p.undoOperationId !== null),
      'Actual inverse counts and parent identity must agree.',
    ))();
export type PricingOperationUndoResult = z.infer<typeof pricingOperationUndoResult>;
export const pricingBatchRejectResult = /* @__PURE__ */ (() =>
  z
    .object({
      protocolVersion: z.literal(1),
      action: z.enum(['bulk_apply', 'operation_undo']),
      selectionHash: pricingRevision,
      revision: pricingRevision,
      reason: z.enum([
        'bulk_stale',
        'bulk_capacity',
        'bulk_overflow',
        'bulk_witness',
        'undo_stale',
        'undo_capacity',
        'undo_witness',
      ]),
    })
    .strict()
    .refine(
      (p) => p.reason.startsWith(p.action === 'bulk_apply' ? 'bulk_' : 'undo_'),
      'The rejection reason must belong to its command action.',
    ))();
